import { Header, Footer } from "../App";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Car, CheckCircle, Camera, Lock, DollarSign, Clock } from "lucide-react";

const HowItWorksScreen = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: Car,
      title: "Enter Your VIN",
      description: "Find your 17-character Vehicle Identification Number on your dashboard or door jamb and enter it to get started."
    },
    {
      icon: CheckCircle,
      title: "Confirm Your Vehicle",
      description: "We'll decode your VIN and show you the details of your vehicle. Confirm it's correct and add your mileage."
    },
    {
      icon: DollarSign,
      title: "Get Your Instant Offer",
      description: "Our algorithm analyzes thousands of comparable vehicles to generate a competitive cash offer in seconds."
    },
    {
      icon: Lock,
      title: "Lock Your Offer",
      description: "Answer questions about your vehicle's condition to get a precise, locked-in offer that won't change."
    },
    {
      icon: Camera,
      title: "Upload Photos",
      description: "Take a few photos of your vehicle to verify its condition and finalize your locked offer."
    },
    {
      icon: Clock,
      title: "Get Paid",
      description: "Schedule an appointment, bring your vehicle, and get paid the same day. It's that simple!"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header variant="light" />
      
      <main className="min-h-[calc(100vh-80px)] py-12 sm:py-20">
        <div className="container-wide">
          {/* Hero */}
          <div className="text-center max-w-2xl mx-auto mb-16 opacity-0 animate-fade-in-up">
            <h1 className="text-heading-1 mb-4">
              How It Works
            </h1>
            <p className="text-body text-lg">
              Getting a cash offer for your vehicle has never been easier. 
              Follow these simple steps to get your instant offer today.
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {steps.map((step, index) => (
              <div 
                key={step.title}
                className="surface-card p-8 opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
              >
                <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                  <step.icon className="w-7 h-7 text-blue-600" />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </span>
                  <h3 className="text-xl font-semibold text-slate-900 font-heading">
                    {step.title}
                  </h3>
                </div>
                <p className="text-slate-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center opacity-0 animate-fade-in-up animate-stagger-5">
            <Button
              onClick={() => navigate('/')}
              className="h-14 px-10 btn-primary"
              data-testid="get-started-btn"
            >
              Get Your Offer Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          {/* FAQ Section */}
          <div className="mt-20 max-w-3xl mx-auto">
            <h2 className="text-heading-2 text-center mb-10">
              Frequently Asked Questions
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "How long does the process take?",
                  a: "You can get an instant offer in as little as 15 seconds after entering your VIN, zip code and mileage. The entire process from final inspection to payment can be completed in as little as one hour at a nearest location."
                },
                {
                  q: "Is the offer guaranteed?",
                  a: "Once you lock your offer and your vehicle condition and ownership are verified at our location, we guarantee the price. We can also help you with your next vehicle, if you're interested in trading your current vehicle."
                },
                {
                  q: "What do I need to bring?",
                  a: "Bring your vehicle, the title, valid ID, and all keys. If you have a loan on the vehicle, bring the payoff information."
                },
                {
                  q: "Can I get an offer for a leased vehicle?",
                  a: "Yes, but you'll need to contact your leasing company for the buyout amount. We can then make you an offer based on that information."
                }
              ].map((faq) => (
                <div key={faq.q} className="surface-card p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HowItWorksScreen;
